import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type {
  Asset,
  TranscriptionMatch as TranscriptionMatchType,
} from '@frameio/graphql-client/generated';
import { Box, Text } from '@frameio/vapor';
import { useSetClientAssetTranscriptionMatch } from '../../../../../../hooks';
import { HighlightText } from '../../../../../../shared/components';
import { useQuickSearchModal } from '../../../../../../shared/contexts';
import { useQuickSearchSupportedAssetMediaV2Fragment } from '../../../../../../shared/gql/generated/QuickSearchSupportedAssetMediaV2';
import type { OnMatchClickHandler } from '../../../../../../shared/types';
import { getAssetResultItemLink } from '../../../../../../shared/utils';
import { getMatchLabel } from '../../../../../../shared/utils/getMatchLabel';
import TEXT from './generated/TEXT/TranscriptionMatchQS';
import { MatchCard, MatchCardsContainer } from './shared';

TEXT.DEFINE({
  /** Accessibility label for viewing transcript match in context */
  label: ({ matchCount }: { matchCount: number }): string =>
    `View transcript match ${matchCount} in context`,
});

type TranscriptionMatchDetailProps = {
  assetId: string;
  transcriptionMatch: TranscriptionMatchType;
  onMatchClick?: OnMatchClickHandler;
};

export const TranscriptionMatch = ({
  assetId,
  transcriptionMatch,
  onMatchClick,
}: TranscriptionMatchDetailProps) => {
  const router = useRouter();
  const { closeModal } = useQuickSearchModal();

  const { data: assetData } =
    useQuickSearchSupportedAssetMediaV2Fragment(assetId);

  const setClientAssetTranscriptionMatch =
    useSetClientAssetTranscriptionMatch();

  const handleTranscriptClick = useCallback(
    (transcriptionId: string, term: string) => {
      onMatchClick?.(transcriptionMatch.__typename ?? 'TranscriptionMatch');
      setClientAssetTranscriptionMatch({
        id: assetId,
        transcriptionId,
        term,
      });
      const href = getAssetResultItemLink(assetData as Asset);
      router.push(href);
      closeModal({ navigatingTo: href });
    },
    [
      assetData,
      router,
      closeModal,
      assetId,
      setClientAssetTranscriptionMatch,
      onMatchClick,
      transcriptionMatch,
    ]
  );

  const transcription = transcriptionMatch.matches?.nodes?.[0];
  const snippetsCount = transcription?.snippets?.length ?? 0;
  const terms = transcription?.terms ?? [];

  if (!transcription) return null;

  return (
    <MatchCardsContainer>
      {transcription?.snippets?.map((snippet, index) => {
        const highlightTerm = terms.find(
          (term) => term && snippet?.includes(term)
        );
        const matchCount = index + 1;
        return (
          <MatchCard
            key={`transcript-${transcription?.id}-${index}`}
            aria-label={TEXT.label({ matchCount })}
            onClick={() => {
              handleTranscriptClick(
                transcription.id ?? '',
                highlightTerm ?? ''
              );
            }}
            role="button"
            tabIndex={0}
          >
            <Box display="flex" flexDirection="column" gap={4}>
              <Text color="primary" fontWeight="medium" variant="secondary">
                {snippetsCount > 1
                  ? `${getMatchLabel(transcription)} ${matchCount}`
                  : getMatchLabel(transcription)}
              </Text>

              {highlightTerm ? (
                <HighlightText
                  color="tertiary"
                  highlights={highlightTerm ?? undefined}
                  text={snippet ?? ''}
                  truncate={false}
                  variant="secondary"
                />
              ) : (
                <Text color="primary" variant="secondary">
                  {snippet}
                </Text>
              )}
            </Box>
          </MatchCard>
        );
      })}
    </MatchCardsContainer>
  );
};
